import { useQuery } from '@tanstack/react-query'
import { BarChartHorizontal } from 'lucide-react'
import {
  Bar,
  BarChart,
  LabelList,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { registrosApi } from '@/modules/registros/api/registros.api'
import type { GraficaBarrasItemConfig } from '@/modules/registros/types'

// De oscuro (teal-800, mismo tono del botón) a claro
const TEAL_PALETTE = [
  '#115e59', // teal-800
  '#0f766e', // teal-700
  '#0d9488', // teal-600
  '#14b8a6', // teal-500
  '#2dd4bf', // teal-400
  '#5eead4', // teal-300
  '#99f6e4', // teal-200
  '#ccfbf1', // teal-100
]

interface DynamicHorizontalBarChartProps {
  config: GraficaBarrasItemConfig
  configId: number
}

export function DynamicHorizontalBarChart({ config, configId }: DynamicHorizontalBarChartProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: [
      'agregacion-barras-horizontal',
      configId,
      config.columna_agrupacion,
      config.limite_categorias,
    ],
    queryFn: () =>
      registrosApi.getAgregacionBarras(
        configId,
        config.columna_agrupacion,
        config.limite_categorias
      ),
    enabled: !!config.columna_agrupacion,
    staleTime: 60000,
  })

  if (isLoading) {
    return (
      <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <BarChartHorizontal className="h-4 w-4 text-muted-foreground" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-full min-h-[200px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className="h-full flex flex-col border border-destructive/50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <BarChartHorizontal className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              {config.titulo || 'Distribución'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  if (data.datos.length === 0) {
    return (
      <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <BarChartHorizontal className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              {config.titulo || 'Distribución'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
      </Card>
    )
  }

  // Build dynamic chart config and data with mixed teal colors
  const chartConfig: ChartConfig = {
    valor: { label: 'Total' },
  }

  const chartData = data.datos.map((item, index) => {
    const key = `cat-${index}`
    const maxIndex = Math.min(TEAL_PALETTE.length - 1, data.datos.length + 1)
    const colorIndex = data.datos.length <= 1 ? 0 : Math.round((index / (data.datos.length - 1)) * maxIndex)
    const color = TEAL_PALETTE[colorIndex]
    chartConfig[key] = {
      label: item.categoria,
      color,
    }
    return {
      ...item,
      displayName:
        item.categoria.length > 22
          ? `${item.categoria.substring(0, 22)}...`
          : item.categoria,
      fill: `var(--color-${key})`,
    }
  })

  const MAX_VISIBLE = 7
  const BAR_HEIGHT = 40
  const containerHeight = MAX_VISIBLE * BAR_HEIGHT + 20
  const chartHeight = chartData.length * BAR_HEIGHT + 20

  return (
    <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700 min-w-0 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <BarChartHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
          <CardTitle className="text-sm font-medium truncate">
            {config.titulo || 'Distribución'}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-w-0 overflow-hidden">
        <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: `${containerHeight}px` }}>
          <ChartContainer config={chartConfig} style={{ height: `${chartHeight}px` }} className="!aspect-auto w-full max-w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{ left: 8, right: 50 }}
          >
            <YAxis
              dataKey="displayName"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={100}
              tick={{ fontSize: 11 }}
              interval={0}
            />
            <XAxis dataKey="valor" type="number" hide />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  formatter={(value, _name, item) => (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{item.payload.categoria}</span>
                      <span className="text-muted-foreground">
                        Cantidad: {Number(value).toLocaleString('es-EC')}
                      </span>
                    </div>
                  )}
                  hideLabel
                />
              }
            />
            <Bar dataKey="valor" layout="vertical" radius={5}>
              <LabelList
                dataKey="valor"
                position="right"
                className="fill-foreground"
                fontSize={12}
                formatter={(value: number) => value.toLocaleString('es-EC')}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
        </div>
      </CardContent>
    </Card>
  )
}
